'use client';

import { Avatar as AvatarPrimitive } from '@base-ui/react/avatar';
import type React from 'react';

import { cn } from './lib/utils';

function Avatar({ className, ...props }: AvatarPrimitive.Root.Props) {
  return (
    <AvatarPrimitive.Root
      className={cn(
        'inline-flex size-(--avatar-size,--spacing(8)) shrink-0 select-none items-center justify-center overflow-hidden rounded-full bg-background align-middle font-medium text-xs',
        className,
      )}
      data-slot="avatar"
      {...props}
    />
  );
}

function AvatarImage({ className, ...props }: AvatarPrimitive.Image.Props) {
  return (
    <AvatarPrimitive.Image
      className={cn('size-full object-cover', className)}
      data-slot="avatar-image"
      {...props}
    />
  );
}

function AvatarFallback({
  className,
  ...props
}: AvatarPrimitive.Fallback.Props) {
  return (
    <AvatarPrimitive.Fallback
      className={cn(
        'flex size-full items-center justify-center rounded-full bg-muted',
        className,
      )}
      data-slot="avatar-fallback"
      {...props}
    />
  );
}

const AVATAR_GROUP_CUT =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 2 2'%3E%3Ccircle cx='1' cy='1' r='1'/%3E%3C/svg%3E\")";

export interface AvatarGroupProps extends React.ComponentProps<'div'> {
  topmost?: 'first' | 'last';
}

export function AvatarGroup({
  className,
  style,
  topmost = 'first',
  ...props
}: AvatarGroupProps): React.ReactElement {
  return (
    <div
      className={cn(
        'flex w-fit items-center',
        '[--avatar-group-size:var(--avatar-size,--spacing(8))]',
        '[--avatar-group-gap:var(--avatar-gap,calc(var(--avatar-group-size)/16))]',
        '[--avatar-group-overlap:var(--avatar-overlap,calc(var(--avatar-group-size)*0.3))]',
        '[--avatar-group-cut-end:calc(var(--avatar-group-size)*1.5-var(--avatar-group-overlap))]',
        '[--avatar-group-cut-radius:calc(var(--avatar-group-size)/2+var(--avatar-group-gap))]',
        '[--avatar-group-cut-start:calc(var(--avatar-group-overlap)-var(--avatar-group-size)/2)]',
        '[--avatar-group-mask-position:0_0,calc(var(--avatar-group-cut-x)-var(--avatar-group-cut-radius))_50%]',
        '[--avatar-group-mask-size:100%_100%,calc(var(--avatar-group-cut-radius)*2)_calc(var(--avatar-group-cut-radius)*2)]',
        '[--avatar-group-mask:none]',
        'supports-[mask-composite:subtract]:[--avatar-group-mask:var(--avatar-group-cut-image),var(--avatar-group-cut-image)]',
        '[--avatar-group-nudge:var(--avatar-nudge,calc((var(--avatar-group-overlap)+var(--avatar-group-gap))*0.3))]',
        '*:not-first:-ms-(--avatar-group-overlap)',
        topmost === 'first'
          ? '*:not-first:mask-(--avatar-group-mask) *:not-first:mask-subtract *:not-first:mask-position-(--avatar-group-mask-position) *:not-first:mask-no-repeat *:not-first:mask-size-(--avatar-group-mask-size) [--avatar-group-cut-x:var(--avatar-group-cut-start)] *:not-first:*:data-[slot=avatar-fallback]:ps-[calc(var(--avatar-group-nudge)*2)] supports-[mask-composite:subtract]:*:not-first:rounded-none rtl:[--avatar-group-cut-x:var(--avatar-group-cut-end)]'
          : '*:not-last:mask-(--avatar-group-mask) *:not-last:mask-subtract *:not-last:mask-position-(--avatar-group-mask-position) *:not-last:mask-no-repeat *:not-last:mask-size-(--avatar-group-mask-size) [--avatar-group-cut-x:var(--avatar-group-cut-end)] *:not-last:*:data-[slot=avatar-fallback]:pe-[calc(var(--avatar-group-nudge)*2)] supports-[mask-composite:subtract]:*:not-last:rounded-none rtl:[--avatar-group-cut-x:var(--avatar-group-cut-start)]',
        className,
      )}
      data-slot="avatar-group"
      data-topmost={topmost}
      style={
        {
          '--avatar-group-cut-image': AVATAR_GROUP_CUT,
          ...style,
        } as React.CSSProperties
      }
      {...props}
    />
  );
}

export { Avatar, AvatarImage, AvatarFallback };
